import { useMemo, useRef, useState, type ReactNode } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { cn } from '@/shared/lib/cn';

/**
 * Generic searchable combobox.
 *
 * Deliberately minimal: the caller provides the full item list; the
 * combobox filters in-memory via `matcher`. Good enough for master-data
 * pickers (contactos, articulos) that never exceed a few thousand rows.
 * For huge/server-paginated lists, build a purpose-specific one instead.
 */
export type ComboboxProps<T> = {
  value: T | null;
  onChange: (next: T | null) => void;
  items: T[];
  /** Stable string id per item, used for keying + "selected" check. */
  getKey: (item: T) => string;
  /** Display string. */
  getLabel: (item: T) => string;
  /** Optional secondary line rendered under the label. */
  getSublabel?: (item: T) => string | null | undefined;
  /** Return true when `item` matches the lowercased search query. */
  matcher: (item: T, query: string) => boolean;
  placeholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  className?: string;
  /** Allow clearing the selection via × button. */
  clearable?: boolean;
  /** Render slot for extra per-item decoration (right-aligned). */
  renderItemRight?: (item: T) => ReactNode;
  /** Per-item class override (e.g. tinted background for out-of-stock rows). */
  getItemClassName?: (item: T) => string | undefined;
};

export function Combobox<T>({
  value,
  onChange,
  items,
  getKey,
  getLabel,
  getSublabel,
  matcher,
  placeholder = 'Seleccionar…',
  emptyMessage = 'Sin resultados.',
  disabled,
  className,
  clearable = true,
  renderItemRight,
  getItemClassName,
}: ComboboxProps<T>) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) => matcher(i, q));
  }, [items, query, matcher]);

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) {
          setQuery('');
          // Focus the search input on open so keyboard users can type immediately.
          setTimeout(() => inputRef.current?.focus(), 0);
        }
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            'flex h-10 w-full items-center justify-between gap-2 rounded-lg border border-transparent bg-secondary px-3 text-left text-sm text-foreground transition-colors focus-visible:border-ring focus-visible:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 disabled:cursor-not-allowed disabled:opacity-50',
            className,
          )}
        >
          <span className={cn('truncate', !value && 'text-muted-foreground')}>
            {value ? getLabel(value) : placeholder}
          </span>
          <span className="flex shrink-0 items-center gap-1">
            {clearable && value && (
              <span
                role="button"
                aria-label="Limpiar selección"
                tabIndex={0}
                className="inline-flex h-5 w-5 items-center justify-center rounded hover:bg-background"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    onChange(null);
                  }
                }}
              >
                <X className="h-3 w-3 text-muted-foreground" aria-hidden />
              </span>
            )}
            <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-2">
        <div className="mb-2 flex shrink-0 items-center gap-2 rounded-md border bg-background px-2">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar…"
            className="h-9 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground">{emptyMessage}</div>
          ) : (
            <ul className="space-y-0.5">
              {filtered.map((item) => {
                const selected = value ? getKey(value) === getKey(item) : false;
                const sub = getSublabel?.(item);
                return (
                  <li key={getKey(item)}>
                    <button
                      type="button"
                      onClick={() => {
                        onChange(item);
                        setOpen(false);
                      }}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm outline-none',
                        'hover:bg-secondary focus-visible:bg-secondary',
                        selected && 'bg-secondary font-medium',
                        getItemClassName?.(item),
                      )}
                    >
                      <Check
                        className={cn(
                          'h-4 w-4 shrink-0',
                          selected ? 'text-primary' : 'text-transparent',
                        )}
                        aria-hidden
                      />
                      <span className="flex-1 overflow-hidden">
                        <span className="block truncate">{getLabel(item)}</span>
                        {sub && (
                          <span className="block truncate text-xs text-muted-foreground">
                            {sub}
                          </span>
                        )}
                      </span>
                      {renderItemRight?.(item)}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
